<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WebhookForm from './WebhookForm.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  webhook: { type: Object, default: null },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const { replaceInstallationName } = useBranding();
const { isAdmin } = useAdmin();

const dialogRef = ref(null);
const formRef = ref(null);
const createdWebhook = ref(null);
const secretVisible = ref(false);

const uiFlags = useMapGetter('webhooks/getUIFlags');
const isEditing = computed(() => !!props.webhook?.id);
const isSubmitting = computed(() =>
  isEditing.value ? uiFlags.value.updatingItem : uiFlags.value.creatingItem
);

const open = () => {
  createdWebhook.value = null;
  secretVisible.value = false;
  dialogRef.value.open();
};
const close = () => dialogRef.value.close();

const handleClose = () => {
  createdWebhook.value = null;
  emit('close');
};

const submit = async () => {
  if (!formRef.value?.isValid) return;

  const payload = formRef.value.payload;
  try {
    if (isEditing.value) {
      await store.dispatch('webhooks/update', {
        id: props.webhook.id,
        ...payload,
      });
      useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.API.SUCCESS_MESSAGE'));
      close();
    } else {
      // The secret is shown only once, right after the webhook is created.
      createdWebhook.value = await store.dispatch('webhooks/create', {
        webhook: payload,
      });
    }
  } catch (error) {
    const key = isEditing.value
      ? 'INTEGRATION_SETTINGS.WEBHOOK.EDIT.API.ERROR_MESSAGE'
      : 'INTEGRATION_SETTINGS.WEBHOOK.ADD.API.ERROR_MESSAGE';
    useAlert(error?.response?.data?.message || t(key));
  }
};

const copySecret = async () => {
  await copyTextToClipboard(createdWebhook.value.secret);
  useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY_SUCCESS'));
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    sticky-header-footer
    :title="
      createdWebhook
        ? $t('INTEGRATION_SETTINGS.WEBHOOK.ADD.API.SUCCESS_MESSAGE')
        : $t(
            isEditing
              ? 'INTEGRATION_SETTINGS.WEBHOOK.EDIT.TITLE'
              : 'INTEGRATION_SETTINGS.WEBHOOK.ADD.TITLE'
          )
    "
    :description="
      createdWebhook
        ? $t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.CREATED_DESC')
        : replaceInstallationName($t('INTEGRATION_SETTINGS.WEBHOOK.FORM.DESC'))
    "
    :show-cancel-button="!createdWebhook"
    :cancel-button-label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.CANCEL')"
    :confirm-button-label="
      createdWebhook
        ? $t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.DONE')
        : $t(
            isEditing
              ? 'INTEGRATION_SETTINGS.WEBHOOK.FORM.EDIT_SUBMIT'
              : 'INTEGRATION_SETTINGS.WEBHOOK.FORM.ADD_SUBMIT'
          )
    "
    :is-loading="isSubmitting"
    @confirm="createdWebhook ? close() : submit()"
    @close="handleClose"
  >
    <div v-if="createdWebhook" class="flex items-end gap-2">
      <div class="flex-1 min-w-0">
        <Input
          :model-value="
            secretVisible
              ? createdWebhook.secret
              : '••••••••••••••••••••••••••••••••'
          "
          readonly
          :label="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.LABEL')"
          custom-input-class="font-mono"
        />
      </div>
      <NextButton
        v-if="isAdmin"
        v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.TOGGLE')"
        type="button"
        :icon="secretVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'"
        slate
        faded
        @click="secretVisible = !secretVisible"
      />
      <NextButton
        v-if="isAdmin"
        v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY')"
        type="button"
        icon="i-lucide-copy"
        slate
        faded
        @click="copySecret"
      />
    </div>
    <WebhookForm v-else ref="formRef" :value="webhook || {}" />
  </Dialog>
</template>
